import type { DataProvider, Identifier } from "ra-core";

import { mockDriver } from "../../../../supabase/functions/_shared/messaging/driver.ts";
import {
  NEW_LEAD_SEQUENCE_NAME,
  plannedSequenceEmails,
} from "../../../../supabase/functions/_shared/sequencePlan.ts";
import { STUDIO_FROM } from "../../../../supabase/functions/_shared/vogelkop.ts";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  Contact,
  FollowUpSequence,
  FollowUpStep,
  Message,
  Sale,
} from "../types";

export const stepSendAt = (startedAt: Date, dayOffset: number) => {
  const when = new Date(startedAt);
  when.setDate(when.getDate() + dayOffset);
  return when;
};

export const sequenceSkipReason = (contact: Contact) => {
  if (contact.do_not_contact) return "Do not contact";
  if (contact.email_unsubscribed) return "Unsubscribed";
  const email = contact.email_jsonb?.find((item) => item.email)?.email;
  if (!email) return "No email";
  return null;
};

export const scheduledStepsToCancel = (
  messages: Pick<Message, "id" | "contact_id" | "status" | "enrollment_id">[],
  contactId: Identifier,
  enrollmentIds: Identifier[],
) => {
  const enrolled = new Set(enrollmentIds.map(String));
  return messages.filter(
    (message) =>
      message.contact_id === contactId &&
      message.status === "scheduled" &&
      message.enrollment_id != null &&
      enrolled.has(String(message.enrollment_id)),
  );
};

type SequenceDelivery = {
  provider: string;
  providerMessageId: string | null;
  status: "sent" | "queued" | "failed" | "delivered";
  sentAt: string | null;
  deliveredAt?: string | null;
};

const sequenceUsesResend = () => import.meta.env.VITE_IS_DEMO !== "true";

const deliverSequenceEmail = async (input: {
  contactId: Identifier;
  to: string;
  subject: string | null;
  body: string;
}): Promise<SequenceDelivery> => {
  if (!sequenceUsesResend()) {
    return mockDriver.send({
      channel: "email",
      to: input.to,
      subject: input.subject,
      body: input.body,
    });
  }
  const { data, error } = await getSupabaseClient().functions.invoke(
    "sequence",
    {
      body: {
        action: "send",
        contact_id: input.contactId,
        to: input.to,
        subject: input.subject,
        body: input.body,
      },
    },
  );
  if (error || !data || data.ok === false || data.status === "failed") {
    return {
      provider: "resend",
      providerMessageId: null,
      status: "failed",
      sentAt: null,
    };
  }
  return {
    provider: data.provider,
    providerMessageId: data.providerMessageId ?? null,
    status: data.status,
    sentAt: data.sentAt ?? null,
    deliveredAt: data.deliveredAt ?? null,
  };
};

export const startFollowUp = async (
  dataProvider: DataProvider,
  input: {
    contact: Contact;
    sequenceId: Identifier;
    steps: FollowUpStep[];
    salesId?: Identifier | null;
    repName: string;
    now?: Date;
  },
) => {
  const reason = sequenceSkipReason(input.contact);
  if (reason) throw new Error(reason);
  const email = input.contact.email_jsonb.find((item) => item.email)?.email;
  if (!email) throw new Error("No email");
  const now = input.now ?? new Date();
  const { data: enrollment } = await dataProvider.create(
    "follow_up_enrollments",
    {
      data: {
        sequence_id: input.sequenceId,
        contact_id: input.contact.id,
        status: "active",
        stop_reason: null,
        started_at: now.toISOString(),
      },
    },
  );
  const planned = plannedSequenceEmails({
    steps: input.steps,
    startedAt: now.toISOString(),
    firstName: input.contact.first_name,
    lastName: input.contact.last_name,
    repName: input.repName,
    propertyAddress: input.contact.property_address,
  });
  for (const step of planned) {
    if (step.sendNow) {
      const result = await deliverSequenceEmail({
        contactId: input.contact.id,
        to: email,
        subject: step.subject,
        body: step.body,
      });
      await dataProvider.create("messages", {
        data: {
          contact_id: input.contact.id,
          sales_id: input.salesId ?? null,
          channel: "email",
          direction: "outbound",
          status: result.status,
          subject: step.subject,
          body: step.body,
          from_address: STUDIO_FROM,
          to_address: email,
          provider: result.provider,
          provider_message_id: result.providerMessageId,
          sent_at: result.sentAt,
          delivered_at: result.deliveredAt ?? null,
          enrollment_id: enrollment.id,
          step_id: step.stepId,
          created_at: now.toISOString(),
        },
      });
    } else {
      await dataProvider.create("messages", {
        data: {
          contact_id: input.contact.id,
          sales_id: input.salesId ?? null,
          channel: "email",
          direction: "outbound",
          status: "scheduled",
          subject: step.subject,
          body: step.body,
          from_address: STUDIO_FROM,
          to_address: email,
          provider: sequenceUsesResend() ? "resend" : "mock",
          scheduled_at: step.scheduledAt,
          enrollment_id: enrollment.id,
          step_id: step.stepId,
          created_at: now.toISOString(),
        },
      });
    }
  }
  return enrollment.id as Identifier;
};

export const stopFollowUps = async (
  dataProvider: DataProvider,
  contactId: Identifier,
  reason: string,
) => {
  const { data: enrollments } = await dataProvider.getList(
    "follow_up_enrollments",
    {
      filter: { contact_id: contactId, status: "active" },
      pagination: { page: 1, perPage: 20 },
      sort: { field: "id", order: "ASC" },
    },
  );
  if (!enrollments.length) return 0;
  const { data: messages } = await dataProvider.getList<Message>("messages", {
    filter: { contact_id: contactId, status: "scheduled" },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" },
  });
  const cancelling = scheduledStepsToCancel(
    messages,
    contactId,
    enrollments.map((enrollment) => enrollment.id),
  );
  for (const message of cancelling) {
    const current = messages.find((item) => item.id === message.id);
    if (!current) continue;
    await dataProvider.update("messages", {
      id: current.id,
      data: { status: "cancelled" },
      previousData: current,
    });
  }
  for (const enrollment of enrollments) {
    await dataProvider.update("follow_up_enrollments", {
      id: enrollment.id,
      data: { status: "stopped", stop_reason: reason },
      previousData: enrollment,
    });
  }
  return cancelling.length;
};

const listArgs = {
  pagination: { page: 1, perPage: 100 },
  sort: { field: "id", order: "ASC" as const },
};

/** Enroll a lead that was just created. Existing leads are left alone. */
export const enrollNewLead = async (
  dataProvider: DataProvider,
  contact: Contact,
) => {
  if (sequenceSkipReason(contact)) return null;
  const { data: sequences } = await dataProvider.getList<FollowUpSequence>(
    "follow_up_sequences",
    { ...listArgs, filter: {} },
  );
  const sequence =
    sequences.find((item) => item.name === NEW_LEAD_SEQUENCE_NAME) ??
    sequences[0];
  if (!sequence) return null;
  const { data: active } = await dataProvider.getList("follow_up_enrollments", {
    ...listArgs,
    filter: { contact_id: contact.id, status: "active" },
  });
  if (active.length) return null;
  const { data: steps } = await dataProvider.getList<FollowUpStep>(
    "follow_up_steps",
    { ...listArgs, filter: { sequence_id: sequence.id } },
  );
  if (!steps.length) return null;
  let repName = "Vogelkop";
  if (contact.sales_id != null) {
    try {
      const { data: sale } = await dataProvider.getOne<Sale>("sales", {
        id: contact.sales_id,
      });
      const name = `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim();
      if (name) repName = name;
    } catch {
      repName = "Vogelkop";
    }
  }
  return startFollowUp(dataProvider, {
    contact,
    sequenceId: sequence.id,
    steps,
    salesId: contact.sales_id ?? null,
    repName,
  });
};

export const stopReplySequence = async (
  dataProvider: DataProvider,
  message: Pick<Message, "direction" | "channel" | "contact_id">,
) => {
  if (message.direction !== "inbound" || message.channel !== "email") return;
  await stopFollowUps(dataProvider, message.contact_id, "They replied");
};
